import { useMemo, useState, type ReactNode } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { NAV, type NavLeaf, type NavNode } from '@/config/nav';
import { api } from '@/lib/api/client';
import { keys } from '@/lib/query/keys';
import { useAuth } from '@/lib/auth/useAuth';
import type { Permission } from '@/lib/rbac/permissions';
import { useLocale } from '@/lib/i18n/useLocale';
import { cn } from '@/lib/ui/cn';
import { editorPath, resolveLabel } from '@/features/pageEditor/outline';
import { useOutlines } from '@/features/pageEditor/useOutlines';
import { IconChevron, IconHome } from '@/features/pageEditor/icons';
import type { DashboardSummary, Locale, PageOutline } from '@/lib/api/types';
import { placePages, recordPlaces } from './places';

const FOLDS_KEY = 'tepco.nav.folds.v1';

/**
 * The console's sidebar. The website comes first, as the website itself: every page in the
 * order of its menu, grouped the way its footer groups them, the open page unfolding into
 * its sections. Everything else (the lists pages show, the inbox, the media library,
 * administration) sits below it, the rarely used groups folded.
 */
export function SidebarNav({ onNavigate }: { onNavigate: () => void }) {
  const { t } = useTranslation();
  const { can } = useAuth();
  const { locale } = useLocale();
  const visible = NAV.map((node) => filterNode(node, can)).filter(Boolean) as NavNode[];
  const canContent = can('content:read');
  const outlines = useOutlines(canContent);
  const list = outlines.data;

  // Unseen visitor messages per inbox, from the summary the overview also reads.
  const canLeads = can('leads:read');
  const summary = useQuery({
    queryKey: keys.dashboard(),
    queryFn: () => api.get<DashboardSummary>('/admin/dashboard'),
    enabled: canLeads,
    staleTime: 120_000,
    refetchOnWindowFocus: false,
  });

  const [folds, setFolds] = useState<Record<string, boolean>>(() => {
    try {
      return JSON.parse(localStorage.getItem(FOLDS_KEY) ?? '{}') as Record<string, boolean>;
    } catch {
      return {};
    }
  });
  const toggleFold = (key: string, open: boolean) =>
    setFolds((prev) => {
      const next = { ...prev, [key]: open };
      try {
        localStorage.setItem(FOLDS_KEY, JSON.stringify(next));
      } catch {
        /* private mode: folds reset next visit */
      }
      return next;
    });

  return (
    <div className="flex flex-col gap-md">
      {visible.map((node) => {
        if (node.type === 'website') {
          return (
            <WebsiteTree
              key="website"
              outlines={list?.outlines ?? null}
              groups={list?.groups ?? []}
              failed={outlines.isError}
              onRetry={() => void outlines.refetch()}
              folds={folds}
              onFold={toggleFold}
              onNavigate={onNavigate}
            />
          );
        }
        if (node.type === 'leaf')
          return <NavItem key={node.navKey} leaf={node} onNavigate={onNavigate} />;
        const heading = t(node.labelKey ?? `nav.${node.navKey}`);
        const items = node.children.map((leaf) => (
          <NavItem
            key={leaf.navKey}
            leaf={leaf}
            onNavigate={onNavigate}
            hint={
              leaf.collection && list
                ? whereShown(list.outlines, leaf.collection, locale, t)
                : undefined
            }
            count={leaf.leadType ? summary.data?.leads?.[leaf.leadType]?.unseen : undefined}
          />
        ));
        if (!node.fold) {
          return (
            <section key={node.navKey} aria-label={heading}>
              <GroupHeading>{heading}</GroupHeading>
              {items}
            </section>
          );
        }
        return (
          <Fold
            key={node.navKey}
            id={`nav-${node.navKey}`}
            label={heading}
            open={folds[node.navKey] ?? node.fold.open}
            onToggle={(open) => toggleFold(node.navKey, open)}
          >
            {items}
          </Fold>
        );
      })}
    </div>
  );
}

/** "on Projects", or "not on the website": where a list's records are seen. */
function whereShown(
  outlines: PageOutline[],
  collection: string,
  locale: Locale,
  t: (k: string, o?: Record<string, unknown>) => string,
) {
  const pages = placePages(recordPlaces(outlines, collection));
  return pages.length === 0
    ? t('website.nav.notOnSite')
    : t('website.nav.onPage', { page: resolveLabel(pages[0].outline.label, locale) });
}

function WebsiteTree({
  outlines,
  groups,
  failed,
  onRetry,
  folds,
  onFold,
  onNavigate,
}: {
  outlines: PageOutline[] | null;
  groups: { key: string; label: { en: string; ar: string } }[];
  failed: boolean;
  onRetry: () => void;
  folds: Record<string, boolean>;
  onFold: (key: string, open: boolean) => void;
  onNavigate: () => void;
}) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const { pathname } = useLocation();
  const [params] = useSearchParams();
  const active = useMemo(
    () => outlines?.find((o) => pathname === editorPath(o)) ?? null,
    [outlines, pathname],
  );

  return (
    <section aria-labelledby="nav-website">
      <GroupHeading id="nav-website">{t('website.nav.website')}</GroupHeading>
      <SideLink to="/" active={pathname === '/' || pathname === '/pages'} onNavigate={onNavigate}>
        <IconHome className="shrink-0 opacity-80" />
        {t('website.nav.allPages')}
      </SideLink>

      {failed && (
        <p className="px-sm py-xs text-small text-sky-blue">
          {t('website.nav.pagesFailed')}{' '}
          <button
            type="button"
            onClick={onRetry}
            className="underline underline-offset-4 hover:text-canvas-paper focus-visible:shadow-focus"
          >
            {t('website.nav.retry')}
          </button>
        </p>
      )}
      {!outlines && !failed && (
        <div
          aria-label={t('website.nav.loadingPages')}
          className="flex flex-col gap-xs px-sm py-xs"
        >
          {[72, 56, 64].map((w) => (
            <span
              key={w}
              className="h-[10px] animate-pulse rounded-xs bg-white/10 motion-reduce:animate-none"
              style={{ width: `${w}%` }}
            />
          ))}
        </div>
      )}

      {outlines &&
        groups.map((g) => {
          const pages = outlines.filter((o) => o.group === g.key);
          if (pages.length === 0) return null;
          const tree = (
            <ul className="flex flex-col">
              {pages.map((o) => (
                <li key={o.key}>
                  <SideLink
                    to={editorPath(o)}
                    active={active?.key === o.key}
                    onNavigate={onNavigate}
                  >
                    <span className="min-w-0 truncate">{resolveLabel(o.label, locale)}</span>
                  </SideLink>
                  {active?.key === o.key && (
                    <Sections
                      outline={o}
                      locale={locale}
                      current={params.get('region')}
                      lang={params.get('lang')}
                      onNavigate={onNavigate}
                    />
                  )}
                </li>
              ))}
            </ul>
          );
          // The special screens are few and rarely edited: folded unless one is open.
          if (g.key === 'screens') {
            return (
              <Fold
                key={g.key}
                id={`nav-pages-${g.key}`}
                label={resolveLabel(g.label, locale)}
                open={folds[`pages-${g.key}`] ?? pages.some((o) => o.key === active?.key)}
                onToggle={(open) => onFold(`pages-${g.key}`, open)}
                quiet
              >
                {tree}
              </Fold>
            );
          }
          // A group of one page named like the group ("Home") needs no heading of its own.
          const lone =
            pages.length === 1 &&
            resolveLabel(pages[0].label, locale) === resolveLabel(g.label, locale);
          return (
            <div key={g.key} className="mt-xs">
              {!lone && (
                <p className="px-sm pb-2xs pt-xs text-label-sm text-sky-blue/60">
                  {resolveLabel(g.label, locale)}
                </p>
              )}
              {tree}
            </div>
          );
        })}
    </section>
  );
}

/** The open page's sections, in the order a visitor meets them. */
function Sections({
  outline,
  locale,
  current,
  lang,
  onNavigate,
}: {
  outline: PageOutline;
  locale: Locale;
  current: string | null;
  /** The language being edited: a section picked here keeps it. */
  lang: string | null;
  onNavigate: () => void;
}) {
  const { t } = useTranslation();
  const label = resolveLabel(outline.label, locale);
  const keepLang = lang === 'en' || lang === 'ar' ? `&lang=${lang}` : '';
  return (
    <ul
      aria-label={t('website.nav.sectionsOf', { page: label })}
      className="mb-xs ms-sm border-s-[0.5px] border-white/15 ps-xs"
    >
      {outline.regions
        .filter((r) => r.on_page)
        .map((r) => {
          const on = current === r.id;
          return (
            <li key={r.id}>
              <Link
                to={`${editorPath(outline, r.id)}${keepLang}`}
                onClick={onNavigate}
                aria-current={on ? 'location' : undefined}
                className={cn(
                  'flex min-h-[32px] items-center rounded-xs px-sm text-small transition-colors duration-almanac focus-visible:shadow-focus',
                  on
                    ? 'bg-white/10 text-canvas-paper'
                    : 'text-sky-blue/90 hover:bg-white/5 hover:text-canvas-paper',
                )}
              >
                <span className="min-w-0 truncate">{resolveLabel(r.label, locale)}</span>
              </Link>
            </li>
          );
        })}
    </ul>
  );
}

function GroupHeading({ id, children }: { id?: string; children: ReactNode }) {
  return (
    <p
      id={id}
      className="px-sm pb-2xs font-mono text-label-sm uppercase tracking-[0.12em] rtl:font-arabic rtl:normal-case rtl:tracking-normal text-sky-blue/60"
    >
      {children}
    </p>
  );
}

/** A folding group: its heading is the button that opens it. */
function Fold({
  id,
  label,
  open,
  onToggle,
  quiet = false,
  children,
}: {
  id: string;
  label: string;
  open: boolean;
  onToggle: (open: boolean) => void;
  quiet?: boolean;
  children: ReactNode;
}) {
  return (
    <section aria-labelledby={`${id}-h`} className={quiet ? 'mt-xs' : undefined}>
      <button
        type="button"
        id={`${id}-h`}
        aria-expanded={open}
        aria-controls={`${id}-body`}
        onClick={() => onToggle(!open)}
        className={cn(
          'flex min-h-[36px] w-full items-center gap-xs rounded-sm px-sm text-start transition-colors duration-almanac hover:bg-white/5 hover:text-canvas-paper focus-visible:shadow-focus',
          quiet
            ? 'text-label-sm text-sky-blue/60'
            : 'font-mono text-label-sm uppercase tracking-[0.12em] rtl:font-arabic rtl:normal-case rtl:tracking-normal text-sky-blue/60',
        )}
      >
        <IconChevron
          className={cn(
            'size-[12px] shrink-0 transition-transform duration-almanac rtl:-scale-x-100',
            open && 'rotate-90 rtl:rotate-90',
          )}
        />
        <span className="min-w-0 flex-1 truncate">{label}</span>
      </button>
      <div id={`${id}-body`} hidden={!open}>
        {children}
      </div>
    </section>
  );
}

function NavItem({
  leaf,
  onNavigate,
  hint,
  count,
}: {
  leaf: NavLeaf;
  onNavigate: () => void;
  hint?: string;
  count?: number;
}) {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const active = leaf.to === '/' ? pathname === '/' : pathname.startsWith(leaf.to);
  const label = t(leaf.labelKey ?? `nav.${leaf.navKey}`);
  return (
    <SideLink to={leaf.to} active={active} onNavigate={onNavigate}>
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="truncate">{label}</span>
        {hint && <span className="truncate text-label-sm text-sky-blue/60">{hint}</span>}
      </span>
      {!!count && count > 0 && (
        <span className="tnum shrink-0 rounded-pill bg-accent-blue px-[7px] py-[1px] font-mono text-label-sm text-canvas-paper">
          {count}
          <span className="sr-only"> {t('website.nav.unseen', { count })}</span>
        </span>
      )}
    </SideLink>
  );
}

function SideLink({
  to,
  active,
  onNavigate,
  children,
}: {
  to: string;
  active: boolean;
  onNavigate: () => void;
  children: ReactNode;
}) {
  return (
    <Link
      to={to}
      onClick={onNavigate}
      aria-current={active ? 'page' : undefined}
      className={cn(
        'flex min-h-[36px] items-center gap-xs rounded-sm px-sm py-[6px] text-small transition-colors duration-almanac focus-visible:shadow-focus',
        active
          ? 'bg-white/10 font-medium text-canvas-paper'
          : 'text-sky-blue hover:bg-white/5 hover:text-canvas-paper',
      )}
    >
      {children}
    </Link>
  );
}

/** Hide nodes the role cannot read; drop empty groups. */
function filterNode(node: NavNode, can: (p: Permission) => boolean): NavNode | null {
  if (node.type === 'leaf' || node.type === 'website') return can(node.permission) ? node : null;
  const children = node.children.filter((c) => can(c.permission));
  return children.length ? { ...node, children } : null;
}
